import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useListAssets } from '@/features/assets/api/list-assets';
import { useListAssetsByService } from '@/features/assets/api/list-assets-by-service';
import { useRejectBatchItem } from '@/features/content-batches/api/reject-batch-item';
import {
  type GenerateGraphicInput,
  useGenerateGraphic,
} from '@/features/graphics/api/generate-graphic';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  Graphic,
  GraphicCategory,
} from '@borradh-workspace/api-client/types';
import type { GraphicLogoPlacement } from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import { DecisionFooter } from './decision-footer';

import { queryKeys } from '@/lib/query-keys';
import { ImageIcon, Pencil, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComposerDecision } from '../composer-gate-context';
import { GraphicStatusCard } from './graphic-status-card';
import { ImagePickerDialog } from './image-picker-dialog';
import {
  describeImageryOrder,
  getDefaultServiceImageIds,
} from './image-picker-library';

interface GraphicDraftPreviewProps {
  /** The tool call this card renders from — a stable id for gate registration. */
  id: string;
  onRespond?: (message: string) => void;
  /**
   * The item this proposal was opened under.
   *
   * A graphic does not exist until Accept, so unlike the video card there is no
   * cut to stamp this with — the item is opened up front precisely so there is.
   * Its attempt 0 carries a null asset until Accept fills it, and that null is
   * how this card knows whether it has already been acted on.
   *
   * Absent on older transcripts, which keep the previous browser-local
   * behaviour rather than being retired on a guess.
   */
  itemId?: string;
  /**
   * The attempt this proposal opened. The card reads ITS asset, not the item's
   * current one — a later edit moves the item on, and reading "current" made
   * an earlier draft card point at a graphic a later turn produced, so two rows
   * showed the same carousel.
   */
  attemptId?: string;
  serviceId: string;
  offerId?: string;
  title?: string;
  category: string;
  kind?: string;
  /**
   * The curated style asked for. Accept forwards it as `templateSlug`; without
   * it the render picks its own kind at random and applies no style.
   */
  style?: string;
  /** Slots to offer for a carousel. Defaults to the planner's own count. */
  slideCount?: number;
  /**
   * Library images matching an upload the owner named in words. The card opens
   * on these rather than the tool answering with a list of filenames to type
   * back — which is what it used to do, with no card at all.
   */
  candidateAssetIds?: string[];
  sourceAssetIds?: string[];
  topicSummary?: string;
  /**
   * The owner's requirements for this graphic (border, exact text, added
   * elements…), combined by Claire into one brief. Sent with Accept so the
   * single render honours all of them.
   */
  refinementInstruction?: string;
  includeLogo?: boolean;
  logoPlacement?: GraphicLogoPlacement;
  imageryPolicy?: GenerateGraphicInput['imageryPolicy'];
  imageMode?: GenerateGraphicInput['imageMode'];
  fields?: Array<{ label: string; value: string }>;
  /**
   * This card is not the newest one for the item — a later `patchContent`
   * card exists further down the transcript. Passed straight through to the
   * `GraphicStatusCard` this renders into once accepted: no "selected"
   * highlight, and no `PendingReRoll` of its own, because a later card owns
   * that decision now. See `GraphicStatusCard`'s own doc comment.
   */
  suppressSelectedHighlight?: boolean;
  /**
   * Called with this card's `id` once the proposal is accepted or rejected.
   * Claire wrote "…then hit Accept to generate it" for the proposal; those
   * buttons are gone and the card now says what happened, so the bubble stops
   * showing that instruction (and only that — see `withoutStaleProposalReply`).
   * The stored message is never edited: Claude signs its thinking over earlier
   * turns.
   */
  onReplyStale?: (toolCallId: string) => void;
}

/**
 * What the planner uses when nothing says otherwise. Mirrors
 * `DEFAULT_CAROUSEL_SLIDE_COUNT`; the card cannot import from the features
 * package, and the tool sends the real value on the presentation anyway — this
 * is only the floor for older transcripts written before it did.
 */
const FALLBACK_SLIDE_COUNT = 5;
const MAX_SOURCE_IMAGES = 10;

export function GraphicDraftPreviewCard({
  id,
  onRespond,
  itemId,
  attemptId,
  serviceId,
  offerId,
  title = 'Graphic draft',
  category,
  kind,
  style,
  slideCount,
  candidateAssetIds,
  sourceAssetIds,
  topicSummary,
  refinementInstruction,
  includeLogo,
  logoPlacement,
  imageryPolicy,
  imageMode,
  fields = [],
  suppressSelectedHighlight,
  onReplyStale,
}: GraphicDraftPreviewProps) {
  const isCarousel = kind === 'carousel';
  const slots = isCarousel ? (slideCount ?? FALLBACK_SLIDE_COUNT) : 1;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /**
   * A carousel is not "some images"; it is slide 1, slide 2, slide 3. The flat
   * list cannot say which photograph belongs where once a slide is left empty,
   * so a deck keeps its choices POSITIONALLY and `null` means "you choose".
   */
  const [slidePins, setSlidePins] = useState<(string | null)[]>(() =>
    Array.from({ length: slots }, () => null)
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Which slide the picker is filling; `null` is the whole-graphic picker. */
  const [pickerSlide, setPickerSlide] = useState<number | null>(null);
  /**
   * Permission to INVENT imagery, and nothing more.
   *
   * OFF by default, which is the behaviour this card has always had — it used
   * to send `allowAiImages: false` as a literal, so an org whose service has no
   * uploads fell through to the curated library with no way to ask for
   * anything else.
   *
   * It never competes with a chosen photograph: every imagery policy tries the
   * org's own linked assets FIRST. This only decides what happens once the real
   * tiers come back empty — invent a fitting image, or leave the slot to a
   * brand-colour panel.
   */
  const [aiOverride, setAllowAiImages] = useState<boolean | undefined>();
  /**
   * Permission to use the shared curated LIBRARY — someone else's photograph,
   * licensed, of somewhere that is not this business.
   *
   * ON by default, matching the wire contract and every previous render from
   * this card (it never sent the field, and an unsent `allowStockImages` is
   * read as true). Turning it OFF with AI also off is the strictest setting
   * available — `own-only`, the org's real photos or a text-led design — and
   * it was unreachable from Claire's card until now, which is the exact thing
   * owners ask for when they say "only use real pictures of me".
   */
  const [stockOverride, setAllowStockImages] = useState<boolean | undefined>();
  const [graphic, setGraphic] = useState<Graphic | null>(null);
  // Spent, not unmounted — a card that vanishes on click takes the only
  // acknowledgement of the click with it.
  const [rejectedLocally, setRejected] = useState(false);
  const finishReject = () => {
    setRejected(true);
    onRespond?.(`Don't create ${title ? `“${title}”` : 'this graphic'}.`);
  };
  const { rejectBatchItem, isRejecting } = useRejectBatchItem({
    onSuccess: finishReject,
  });
  const reject = () => {
    if (isRejecting || isGenerating) return;
    if (itemId) rejectBatchItem(itemId);
    else finishReject();
  };
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const initializedServiceRef = useRef<string | null>(null);
  const userChangedSelectionRef = useRef(false);
  const { assets } = useListAssets({
    type: 'image',
    source: 'raw',
    limit: 100,
  });
  const { assets: serviceAssets, isLoading: serviceAssetsLoading } =
    useListAssetsByService(serviceId, 'image');
  const assetById = useMemo(
    () => new Map(assets.map((asset) => [asset.id, asset])),
    [assets]
  );
  const { generateGraphicAsync, isGenerating } = useGenerateGraphic({
    onSuccess: setGraphic,
  });

  // Has this proposal already been accepted? The one fact the card cannot know
  // about itself — React state forgets it on every remount, and a remount used
  // to hand the Accept button back over a graphic that had already been made.
  // Pressing it again generates a SECOND one and spends a second render.
  const { data: itemState } = useQuery({
    queryKey: queryKeys.contentBatches.itemState(itemId ?? '', attemptId),
    queryFn: () =>
      apiClient.get<{
        assetId: string | null;
        reviewStatus?: string;
        proposalGraphicOptions?: Pick<
          GenerateGraphicInput,
          | 'imageMode'
          | 'imageryPolicy'
          | 'includeLogo'
          | 'logoPlacement'
          | 'sourceAssetIds'
        > | null;
      }>(
        // `attemptNumber=0` and not just the stamp. This card OPENED its item,
        // so the content it produced is attempt 0 by construction — and unlike
        // `attemptId` that needs nothing stored in the card to say so. Tool
        // outputs are persisted, so a card written before the stamp existed can
        // never gain one; without this it keeps reading the live cut and points
        // at whatever a later turn produced.
        `content-batches/items/${itemId}/state?attemptNumber=0${
          attemptId ? `&attemptId=${encodeURIComponent(attemptId)}` : ''
        }`
      ),
    enabled: Boolean(itemId),
    staleTime: 10_000,
  });
  const rejected = rejectedLocally || itemState?.reviewStatus === 'rejected';
  const alreadyAccepted = Boolean(itemState?.assetId);
  const answered = rejected || alreadyAccepted || Boolean(graphic);
  useEffect(() => {
    if (answered) onReplyStale?.(id);
  }, [answered, onReplyStale, id]);
  // Between mount and the first answer we do not yet know whether this draft
  // was already accepted. Offering Accept in that window is offering a second
  // render — brief, but a double-press is exactly the kind of thing that fits
  // in a brief window.
  const acceptedUnknown = Boolean(itemId) && itemState === undefined;
  const savedPolicy =
    imageryPolicy ?? itemState?.proposalGraphicOptions?.imageryPolicy;
  const savedSourceIds = itemState?.proposalGraphicOptions?.sourceAssetIds;
  const savedMode = itemState?.proposalGraphicOptions?.imageMode ?? imageMode;
  const assemblesPhotos = savedMode === 'assemble';
  const chosenIds =
    isCarousel && !assemblesPhotos
      ? slidePins.filter((id): id is string => Boolean(id))
      : selectedIds;
  const missingAssemblyImages = assemblesPhotos && chosenIds.length < 2;
  const allowAiImages =
    !assemblesPhotos &&
    (aiOverride ??
      (savedPolicy === 'own-then-ai' ||
        savedPolicy === 'own-then-stock-then-ai' ||
        (savedPolicy === 'ai-only' && chosenIds.length === 0)));
  const allowStockImages =
    !assemblesPhotos &&
    (stockOverride ??
      !['own-only', 'own-then-ai', 'ai-only', 'text-led'].includes(
        savedPolicy ?? ''
      ));
  const activePolicy: GenerateGraphicInput['imageryPolicy'] = allowStockImages
    ? allowAiImages
      ? 'own-then-stock-then-ai'
      : 'own-then-stock'
    : allowAiImages
      ? savedPolicy === 'ai-only' && chosenIds.length === 0
        ? 'ai-only'
        : 'own-then-ai'
      : savedPolicy === 'text-led' && chosenIds.length === 0
        ? 'text-led'
        : 'own-only';

  useEffect(() => {
    if (
      serviceAssetsLoading ||
      acceptedUnknown ||
      (!assemblesPhotos &&
        (savedPolicy === 'ai-only' || savedPolicy === 'text-led')) ||
      initializedServiceRef.current === serviceId ||
      userChangedSelectionRef.current
    ) {
      return;
    }

    initializedServiceRef.current = serviceId;

    // A named reference outranks the service default: the owner said WHICH
    // photographs, so those are the ones to start from.
    const seedSource =
      sourceAssetIds ??
      savedSourceIds ??
      (candidateAssetIds?.length
        ? candidateAssetIds
        : assemblesPhotos
          ? []
          : getDefaultServiceImageIds(serviceAssets));
    // A broad name match can return more candidates than the generation
    // contract accepts. De-duplicate before seeding and keep a single graphic
    // within the same max-10 boundary enforced by the picker and API.
    const seed = [...new Set(seedSource)].slice(
      0,
      isCarousel && !assemblesPhotos ? slots : MAX_SOURCE_IMAGES
    );

    setSelectedIds(seed);
    if (isCarousel) {
      // Positional, so slide 1 gets the first, slide 2 the second, and any
      // slide past the end stays open for us to fill.
      setSlidePins(
        Array.from({ length: slots }, (_unused, index) => seed[index] ?? null)
      );
    }
  }, [
    serviceAssets,
    serviceAssetsLoading,
    serviceId,
    candidateAssetIds,
    sourceAssetIds,
    savedSourceIds,
    isCarousel,
    slots,
    acceptedUnknown,
    savedPolicy,
    assemblesPhotos,
  ]);

  /**
   * A reference the owner named but we could not pin down opens the picker
   * itself. The tool used to answer this in prose — a list of filenames, and a
   * request to type one back — which is the whole of ENG-894.
   */
  const pickerAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (pickerAutoOpenedRef.current || !candidateAssetIds?.length) return;
    pickerAutoOpenedRef.current = true;
    setPickerSlide(isCarousel && !assemblesPhotos ? 0 : null);
    setPickerOpen(true);
  }, [candidateAssetIds, isCarousel, assemblesPhotos]);

  // What the owner has actually chosen, however they chose it. Computed
  // ahead of the early returns below because `sentenceNode`/`startGeneration`
  // (built from it) feed `ComposerDecision`, and hooks cannot follow a
  // conditional return.
  const startGeneration = async () => {
    if (isGenerating || acceptedUnknown || missingAssemblyImages) return;
    setPickerOpen(false);
    await generateGraphicAsync({
      // Fills the proposal rather than opening a second item for the same
      // graphic — and it is what makes `alreadyAccepted` true afterwards.
      ...(itemId ? { itemId } : {}),
      serviceId,
      ...(offerId ? { offerId, usageType: 'ad' as const } : {}),
      ...(savedMode ? { imageMode: savedMode } : {}),
      category: category as GraphicCategory,
      ...(kind === 'single' || kind === 'carousel' ? { kind } : {}),
      // The style the owner asked for. Absent, the server re-derives the kind
      // by coin flip and renders no style at all.
      ...(style && !assemblesPhotos ? { templateSlug: style } : {}),
      ...(topicSummary ? { topicSummary } : {}),
      ...(refinementInstruction ? { refinementInstruction } : {}),
      ...(assemblesPhotos
        ? { includeLogo: false }
        : (includeLogo ?? itemState?.proposalGraphicOptions?.includeLogo) !==
            undefined
          ? {
              includeLogo:
                includeLogo ?? itemState?.proposalGraphicOptions?.includeLogo,
            }
          : {}),
      ...((logoPlacement ?? itemState?.proposalGraphicOptions?.logoPlacement) &&
      !assemblesPhotos
        ? {
            logoPlacement:
              logoPlacement ?? itemState?.proposalGraphicOptions?.logoPlacement,
          }
        : {}),
      imageryPolicy: activePolicy,
      /**
       * A designed deck sends POSITIONS, a single sends a list. Exact assembly
       * also sends a list: each selected photo is the complete next slide.
       *
       * Deliberately not both: `sourceAssetIds` doubles as the deck's photo
       * POOL, so sending the pins there too would make the pool exactly the
       * slides already spoken for, and every unfilled slide would fall to
       * stock. The pins are validated and recorded server-side from
       * `slideAssetIds` alone.
       */
      ...(assemblesPhotos
        ? { sourceAssetIds: chosenIds }
        : isCarousel
          ? chosenIds.length > 0
            ? { slideAssetIds: slidePins }
            : {}
          : selectedIds.length > 0
            ? { sourceAssetIds: selectedIds }
            : {}),
      // The flags remain compatible with older clients. The named policy also
      // preserves AI-only proposals, which intentionally exclude library photos.
      allowAiImages,
      allowStockImages,
    }).catch(() => undefined);
  };

  const sentenceNode = (
    <>
      Create <span className="font-medium">“{title}”</span>
      {isCarousel
        ? assemblesPhotos && chosenIds.length === 0
          ? ' as a carousel of unchanged photos'
          : ` as a ${assemblesPhotos ? chosenIds.length : slots}-slide carousel`
        : ''}
      ?
      {(isCarousel ? chosenIds.length : selectedIds.length) > 0 && (
        <>
          {' '}
          {isCarousel ? chosenIds.length : selectedIds.length} image
          {(isCarousel ? chosenIds.length : selectedIds.length) === 1
            ? ''
            : 's'}{' '}
          selected.
        </>
      )}
    </>
  );

  if (graphic) {
    return (
      <GraphicStatusCard
        itemId={itemId}
        data={{
          graphicId: graphic.id,
          status: graphic.status,
          title: graphic.title ?? undefined,
        }}
        suppressSelectedHighlight={suppressSelectedHighlight}
        serviceId={serviceId}
      />
    );
  }

  // Accepted in an EARLIER session — the graphic exists, but the state that
  // knew about it died with the last mount.
  //
  // `createContent` returns a `graphic_draft` card and no graphicId, because the
  // graphic is not made until Accept. So on reload the tool output alone can
  // never say what this draft produced; the ITEM can, and it is why the item is
  // opened before the content exists. Handing the id to the status card gets
  // the artifact row back, with its title and thumbnail, exactly as it looked
  // when it was made.
  //
  // Without this the card correctly refused to generate a second copy and then
  // showed nothing at all — the owner reopened the conversation to find a
  // spent draft and no sign of the graphic it had made.
  if (alreadyAccepted && itemState?.assetId) {
    return (
      <GraphicStatusCard
        itemId={itemId}
        data={{ graphicId: itemState.assetId }}
        suppressSelectedHighlight={suppressSelectedHighlight}
        serviceId={serviceId}
      />
    );
  }

  if (rejected) {
    return (
      <p
        data-testid="claire-card-graphic-draft"
        className="text-xs text-muted-foreground"
      >
        Discarded. Nothing was generated.
      </p>
    );
  }

  // Unique per card, so two draft cards in one transcript cannot share a
  // switch id — a duplicate id makes the label toggle the wrong card's switch.
  const switchScope = itemId ?? serviceId;
  const visibleFields = fields.filter(
    (field) => field.label.toLowerCase() !== 'category'
  );
  const imageryOrder =
    activePolicy === 'ai-only'
      ? 'AI-generated imagery only.'
      : describeImageryOrder({
          hasChosenImages: chosenIds.length > 0,
          allowStockImages,
          allowAiImages,
        });

  return (
    <ComposerDecision
      id={id}
      gate={
        !rejected
          ? {
              sentence: sentenceNode,
              onAccept: () => void startGeneration(),
              onReject: reject,
              acceptDisabled: acceptedUnknown || missingAssemblyImages,
              acceptDisabledReason: missingAssemblyImages
                ? 'Choose at least two uploaded images.'
                : undefined,
            }
          : null
      }
    >
      <div
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
        data-testid="claire-card-graphic-draft"
      >
        <div className="flex items-start justify-between gap-3 text-xs">
          <div className="flex min-w-0 items-start gap-2">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            <p>{sentenceNode}</p>
          </div>
          {!assemblesPhotos && (
            <button
              type="button"
              onClick={() => setSourcesOpen(true)}
              disabled={isGenerating}
              aria-label="Image sources"
              className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-60"
            >
              Sources
              <Pencil className="size-2.5" />
            </button>
          )}
        </div>

        {visibleFields.length > 0 && (
          <dl className="mt-1 space-y-1 pl-5.5">
            {visibleFields.map((field) => (
              <div
                key={field.label}
                className={
                  field.label === 'Brief'
                    ? 'space-y-0.5 text-xs'
                    : 'flex items-baseline justify-between gap-3 text-xs'
                }
              >
                <dt className="shrink-0 text-muted-foreground">
                  {field.label}
                </dt>
                <dd
                  className={
                    field.label === 'Brief'
                      ? 'whitespace-pre-wrap break-words font-medium'
                      : 'truncate text-right font-medium'
                  }
                >
                  {field.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-2 pl-5.5">
          {/* A DECK IS POSITIONAL. One tile per slide, each opening the picker
            for that slide alone, because "these five photos" and "this photo
            on slide 3" are different instructions and only the card can tell
            them apart. Empty slots are not a blocker — the renderer fills them
            the way it always has. */}
          {isCarousel && !assemblesPhotos ? (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {slidePins.map((pinnedId, index) => {
                const asset = pinnedId ? assetById.get(pinnedId) : undefined;
                const slideLabel = `Slide ${index + 1}`;
                return (
                  <div
                    key={`slide-${index}`}
                    className="relative w-16 shrink-0"
                    data-testid={`graphic-draft-slide-${index}`}
                  >
                    <button
                      type="button"
                      disabled={isGenerating}
                      onClick={() => {
                        setPickerSlide(index);
                        setPickerOpen(true);
                      }}
                      aria-label={
                        pinnedId
                          ? `Change the image for ${slideLabel}`
                          : `Choose an image for ${slideLabel}`
                      }
                      className={`relative block h-14 w-full overflow-hidden rounded-md border text-left transition-colors ${
                        pinnedId
                          ? 'border-primary'
                          : 'border-dashed hover:border-muted-foreground/40'
                      } bg-muted disabled:opacity-60`}
                    >
                      {pinnedId ? (
                        asset?.blobUrl || asset?.thumbnailUrl ? (
                          <img
                            src={asset.thumbnailUrl ?? asset.blobUrl}
                            alt="Selected source"
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className="grid size-full place-items-center">
                            <ImageIcon className="size-4 text-muted-foreground/40" />
                          </div>
                        )
                      ) : (
                        <div className="grid size-full place-items-center px-1 text-center">
                          <span className="text-[9px] leading-tight text-muted-foreground">
                            We&apos;ll choose
                          </span>
                        </div>
                      )}
                      <span className="absolute left-1 top-1 rounded bg-background/85 px-1 text-[9px] font-medium">
                        {index + 1}
                      </span>
                    </button>
                    {pinnedId && !isGenerating && (
                      <button
                        type="button"
                        aria-label={`Clear the image for ${slideLabel}`}
                        onClick={() => {
                          userChangedSelectionRef.current = true;
                          setSlidePins((current) =>
                            current.map((id, i) => (i === index ? null : id))
                          );
                        }}
                        className="absolute -right-1 -top-1 grid size-4 place-items-center rounded-full border bg-background text-muted-foreground hover:text-foreground"
                      >
                        <X className="size-2.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : /* NOT a chooser, and not a blocker.
            This used to be an empty dashed box reading "Choose images" with
            Generate underneath it, so an org with nothing uploaded for this
            service — most of them — was asked to go and find images before it
            would do anything. The renderer has a service-aware fallback and
            builds these graphics perfectly well without uploads; the empty
            state was a chore invented by the card, not a real requirement. */
          selectedIds.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No images selected. Choose images to include in this graphic.
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedIds.map((id) => {
                const asset = assetById.get(id);
                return (
                  <div
                    key={id}
                    className="relative h-14 w-16 shrink-0 overflow-hidden rounded-md border bg-muted"
                  >
                    {asset?.blobUrl || asset?.thumbnailUrl ? (
                      <img
                        src={asset.thumbnailUrl ?? asset.blobUrl}
                        alt="Selected source"
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="grid size-full place-items-center">
                        <ImageIcon className="size-4 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Dialog open={sourcesOpen} onOpenChange={setSourcesOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Image sources</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="grid gap-0.5">
                  <Label htmlFor={`allow-stock-images-${switchScope}`}>
                    Allow stock photos
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Licensed library images — real photographs, but not of this
                    business.
                  </p>
                </div>
                <Switch
                  id={`allow-stock-images-${switchScope}`}
                  checked={allowStockImages}
                  onCheckedChange={setAllowStockImages}
                  disabled={isGenerating}
                />
              </div>

              <div className="flex items-start justify-between gap-3 border-t pt-3">
                <div className="grid gap-0.5">
                  <Label htmlFor={`allow-ai-images-${switchScope}`}>
                    Allow AI-generated imagery
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Invented images. Never presented as this business&apos;s own
                    results.
                  </p>
                </div>
                <Switch
                  id={`allow-ai-images-${switchScope}`}
                  checked={allowAiImages}
                  onCheckedChange={setAllowAiImages}
                  disabled={isGenerating}
                />
              </div>

              {/* The switches say what is PERMITTED; this says what will
                actually happen, in order. Two independent toggles quietly
                describe four different behaviours, and "own-only" in
                particular is easy to select by accident and impossible to
                recognise from the toggles alone. */}
              <p className="border-t pt-3 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Order:</span>{' '}
                {imageryOrder}
              </p>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button>Done</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* A generated design can choose its own imagery. Exact assembly needs
          at least two selected photos; the same approval controls handle both. */}
        {rejected ? (
          <p className="mt-4 text-[11px] text-muted-foreground">
            Discarded. Nothing was generated.
          </p>
        ) : (
          <div className="mt-2">
            <DecisionFooter
              hideSentence
              sentence={sentenceNode}
              onAccept={() => void startGeneration()}
              onReject={reject}
              acceptLabel={isGenerating ? 'Starting…' : 'Accept'}
              onEdit={
                !isCarousel || assemblesPhotos
                  ? () => {
                      setPickerSlide(null);
                      setPickerOpen(true);
                    }
                  : undefined
              }
              editLabel="Change images"
              busy={isGenerating || isRejecting}
              acceptDisabled={acceptedUnknown || missingAssemblyImages}
              acceptDisabledReason={
                missingAssemblyImages
                  ? 'Choose at least two uploaded images.'
                  : undefined
              }
            />
          </div>
        )}

        {!isGenerating && (
          <ImagePickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            // One slide is a one-slot picker; anything else is the old list.
            selectedIds={
              pickerSlide === null
                ? selectedIds
                : [slidePins[pickerSlide]].filter((id): id is string =>
                    Boolean(id)
                  )
            }
            minCount={pickerSlide === null ? (assemblesPhotos ? 2 : 1) : 0}
            maxCount={pickerSlide === null ? 10 : 1}
            title={
              pickerSlide === null
                ? 'Choose images'
                : `Choose an image for slide ${pickerSlide + 1}`
            }
            confirmLabel={
              pickerSlide === null ? 'Use selected images' : 'Use this image'
            }
            onConfirm={(ids) => {
              userChangedSelectionRef.current = true;
              if (pickerSlide === null) {
                setSelectedIds(ids);
                return;
              }
              setSlidePins((current) =>
                current.map((id, i) =>
                  i === pickerSlide ? (ids[0] ?? null) : id
                )
              );
            }}
            serviceId={serviceId}
          />
        )}
      </div>
    </ComposerDecision>
  );
}
